import React, { useEffect, useState } from 'react';
import {
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  OrderedOrderHeaderViewModel,
  OrderedPurchaseLineViewModel,
} from '../models/orderedModel';
import useWindowsDimension from 'functions/useWindowsDimension';
import { t } from 'i18next';
import { MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import CustomAvatar from 'components/ui/customAvatar';

type Props = {
  order: OrderedOrderHeaderViewModel | null;
  loading?: boolean;
  handleChange: (
    field: 'trackingNo' | 'vendorOrderNumber',
    value: string
  ) => void;
  /** Mottar DELTA (mottatt nå) pr. purchase-line */
  handleChangeDelivered: (
    line: OrderedPurchaseLineViewModel,
    value: string
  ) => void;
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

export const OrderedRightSideItem: React.FC<Props> = ({
  order,
  loading = false,
  handleChange,
  handleChangeDelivered,
}) => {
  const [trackingNo, setTrackingNo] = useState('');
  const [vendorOrderNumber, setVendorOrderNumber] = useState('');
  const { height } = useWindowsDimension();

  // “mottatt nå” per purchase-line (key = orderPurchaseLineId)
  const [receiveNow, setReceiveNow] = useState<Record<string, number>>({});

  // Sync headerfelter
  useEffect(() => {
    setTrackingNo(order?.trackingNo ?? '');
    setVendorOrderNumber(order?.vendorOrderNumber ?? '');
  }, [order?.trackingNo, order?.vendorOrderNumber, order?.orderId]);

  // Hent robust lineId
  const getLineId = (line: OrderedPurchaseLineViewModel): string =>
    ((line as any).orderPurchaseLineId ??
      (line as any).orderPostId ??
      (line as any).id ??
      '') as string;

  // Reset “mottatt nå” når valgt ordre byttes
  useEffect(() => {
    const init: Record<string, number> = {};
    (order?.lines ?? []).forEach((l) => (init[l.orderPurchaseLineId] = 0));
    setReceiveNow(init);
  }, [order?.orderId]);

  if (!order) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant='h6'>Ingen ordre valgt</Typography>
        <Typography variant='body2' sx={{ opacity: 0.8 }}>
          Velg en ordre i listen til venstre for å se detaljer.
        </Typography>
      </Paper>
    );
  }

  const lines = order.lines ?? [];

  return (
    <Stack spacing={2}>
      {/* Frakt & referanser */}
      <Paper sx={{ p: 2, borderRadius: 3, backgroundColor: 'primary.main' }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
          Frakt & referanser
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Trackingnummer'
              value={trackingNo}
              onChange={(e) => {
                setTrackingNo(e.target.value);
                handleChange('trackingNo', e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Leverandørens ordre-nummer'
              value={vendorOrderNumber}
              onChange={(e) => {
                setVendorOrderNumber(e.target.value);
                handleChange('vendorOrderNumber', e.target.value);
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Linjer */}
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
          Linjer ({lines.length})
        </Typography>
        <Divider sx={{ my: 1 }} />

        <Stack spacing={1.5} sx={{ height: height - 700, overflowY: 'auto' }}>
          {lines.map((line) => {
            const ordered = Number(line.orderedQuantity ?? 0);
            const delivered = Number(line.receivedQuantity ?? 0);
            const rest = Math.max(0, ordered - delivered);
            const valueNow = receiveNow[line.orderPurchaseLineId] ?? 0;

            const partial = delivered > 0 && rest > 0;
            const done = rest === 0 && ordered > 0;

            return (
              <Paper
                key={line.orderPurchaseLineId}
                sx={{ p: 1, borderRadius: 2, backgroundColor: 'primary.main' }}
              >
                <Grid container spacing={1} alignItems='center'>
                  {/* "Bilde": vi har ikke produktbilde i line-modellen – vis leverandør/logo hvis finnes, evt. avatar på companyName */}
                  <Grid item xs='auto'>
                    {order.vendor?.url ? (
                      <Tooltip title={order.vendor?.name}>
                        <img
                          src={order.vendor?.url || MissingPhoto.COMPANY_URL}
                          style={{
                            borderRadius: 5,
                            display: 'flex',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            userSelect: 'none',
                            width: 120,
                            height: 120,
                            objectFit: 'scale-down',
                          }}
                          alt='vendor'
                        />
                      </Tooltip>
                    ) : (
                      <CustomAvatar
                        avatarName={
                          order.vendor?.name || line.companyName || ''
                        }
                        imgWidth={120}
                        imgHeight={120}
                      />
                    )}
                  </Grid>

                  {/* Midtpanel: company + antall/pris-info */}
                  <Grid item xs={true} sx={{ textAlign: 'left' }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography
                        sx={{
                          fontSize: 22,
                          flexGrow: 1,
                          fontWeight: 'bold',
                          letterSpacing: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={line.companyName ?? ''}
                      >
                        {line.companyName ?? order.vendor?.name ?? '—'}
                      </Typography>

                      <Chip
                        color={
                          done ? 'success' : partial ? 'warning' : 'default'
                        }
                        variant={done ? 'filled' : 'outlined'}
                        label={
                          done
                            ? t('Fullført')
                            : partial
                            ? t('Delvis mottatt')
                            : t('Ikke mottatt')
                        }
                        size='small'
                      />
                    </Stack>

                    {/* Mengde (LEVERT / BESTILT) */}
                    <Stack direction='row' spacing={0} sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          width: 140,
                          textAlign: 'right',
                          pr: 1,
                        }}
                      >
                        {t('bluu.quantity')}
                      </Typography>
                      <Stack direction='row' spacing={0} sx={{ flexGrow: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            flexGrow: 1,
                            textAlign: 'right',
                            pr: 0.5,
                          }}
                        >
                          {delivered}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 16, width: 24, textAlign: 'center' }}
                        >
                          /
                        </Typography>
                        <Typography sx={{ fontSize: 16, textAlign: 'right' }}>
                          {ordered}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 16,
                          width: 40,
                          textAlign: 'right',
                          pr: 0.5,
                        }}
                      >
                        {t('bluu.quantityAbbr').toLowerCase()}
                      </Typography>
                    </Stack>

                    {/* Pris (enhetspris / utsalgspris) */}
                    <Stack direction='row' spacing={0} sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          width: 140,
                          textAlign: 'right',
                          pr: 1,
                        }}
                      >
                        {t('bluu.costPrice')}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 16, flexGrow: 1, textAlign: 'right' }}
                      >
                        {Number(line.unitPrice ?? 0).toFixed(2)}{' '}
                        {t('bluu.currency').toLowerCase()}
                      </Typography>
                    </Stack>

                    <Stack direction='row' spacing={0}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          width: 140,
                          textAlign: 'right',
                          pr: 1,
                        }}
                      >
                        {t('bluu.retailPrice')}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 16,
                          flexGrow: 1,
                          textAlign: 'right',
                          color:
                            Number(line.unitPrice ?? 0) >
                            Number(line.retailPrice ?? 0)
                              ? 'error.main'
                              : 'primary.contrastText',
                        }}
                      >
                        {Number(line.retailPrice ?? 0).toFixed(2)}{' '}
                        {t('bluu.currency').toLowerCase()}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* Nederst i kortet: URL + “Antall mottatt (nå)” */}
                  <Grid item xs={12} sx={{ pl: 1, pb: 0.5, minWidth: 0 }}>
                    <Grid container alignItems='center' wrap='nowrap'>
                      <Grid item xs={true} sx={{ minWidth: 0 }}>
                        <Tooltip title={line.vendorProductUrl ?? ''}>
                          <a
                            href={line.vendorProductUrl ?? '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                              fontSize: 16,
                              letterSpacing: 1,
                              paddingLeft: 4,
                              display: 'block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textDecoration: 'underline',
                              color:
                                (line.vendorProductUrl || '').length > 0
                                  ? 'lightblue'
                                  : 'lightgray',
                            }}
                          >
                            {(line.vendorProductUrl || '').length > 0
                              ? line.vendorProductUrl
                              : t('order.orderPost.noUrl')}
                          </a>
                        </Tooltip>
                      </Grid>

                      <Grid item xs='auto' sx={{ width: 220, ml: 2 }}>
                        <TextField
                          fullWidth
                          size='small'
                          type='number'
                          label='Antall mottatt (nå)'
                          value={valueNow}
                          onFocus={(e) =>
                            (e.target as HTMLInputElement).select()
                          }
                          inputProps={{ min: 0, max: rest }}
                          onChange={(e) => {
                            const raw = Number(e.target.value || 0);
                            const clamped = clamp(raw, 0, rest);
                            setReceiveNow((prev) => ({
                              ...prev,
                              [line.orderPurchaseLineId]: clamped,
                            }));
                          }}
                          onBlur={(e) => {
                            const raw = Number(e.target.value || 0);
                            const clamped = clamp(raw, 0, rest);
                            setReceiveNow((prev) => ({
                              ...prev,
                              [line.orderPurchaseLineId]: clamped,
                            }));
                            // Send DELTA (mottatt nå)
                            handleChangeDelivered(line, String(clamped));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const raw = Number(
                                (e.target as HTMLInputElement).value || 0
                              );
                              const clamped = clamp(raw, 0, rest);
                              setReceiveNow((prev) => ({
                                ...prev,
                                [line.orderPurchaseLineId]: clamped,
                              }));
                              handleChangeDelivered(line, String(clamped));
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </Paper>
    </Stack>
  );
};
